import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import {
  CalendarCheck,
  Map as MapIcon,
  Plane,
  Briefcase,
  Users,
  AlertTriangle,
  LineChart,
  ShoppingBag,
} from "lucide-react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Skeleton } from "@/components/ui/skeleton";
import { getDashboardStats } from "@/lib/admin-dashboard.functions";

const dashboardQuery = queryOptions({
  queryKey: ["admin", "dashboard"],
  queryFn: () => getDashboardStats(),
  staleTime: 30_000,
});

export const Route = createFileRoute("/_authenticated/admin/dashboard")({
  head: () => ({
    meta: [
      { title: "Panel — Mostrando México" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: DashboardPage,
});

function DashboardPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div>
          <h1 className="font-display text-4xl font-semibold">Panel administrativo</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Vista general de reservas, tours y operaciones.
          </p>
        </div>
        <div className="mt-6"><AdminNav /></div>

        <Suspense fallback={<DashboardSkeleton />}>
          <DashboardContent />
        </Suspense>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} className="h-28 rounded-2xl" />
      ))}
    </div>
  );
}

function DashboardContent() {
  const { data } = useSuspenseQuery(dashboardQuery);

  const cards: Array<{ icon: any; label: string; value: any; hint: string; to: string }> = [
    { icon: CalendarCheck, label: "Reservas totales", value: data.bookingsCount, hint: `${data.paidCount} pagadas`, to: "/admin/bookings" },
    { icon: ShoppingBag, label: "Ingresos (MXN)", value: data.totalRevenue.toLocaleString("es-MX", { maximumFractionDigits: 0 }), hint: "Solo reservas pagadas", to: "/admin/analytics" },
    { icon: MapIcon, label: "Tours propios", value: data.toursCount, hint: `${data.activeTours} activos`, to: "/admin/tours" },
    { icon: Briefcase, label: "Productos Bokun", value: data.bokunBookings, hint: "Reservas Bokun", to: "/admin/bokun-status" },
    { icon: Plane, label: "Productos propios", value: data.ownBookings, hint: "Reservas catálogo propio", to: "/admin/tours" },
    { icon: Users, label: "Clientes", value: data.customersCount, hint: "Emails únicos", to: "/admin/customers" },
    { icon: LineChart, label: "Analytics", value: "Ver", hint: "Últimos 30 días", to: "/admin/analytics" },
    { icon: AlertTriangle, label: "Errores", value: "Ver", hint: "Reservas fallidas", to: "/admin/errors" },
  ];

  return (
    <>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <Link
            key={c.label}
            to={c.to}
            className="block rounded-2xl border border-border bg-card p-5 shadow-card transition-colors hover:border-primary/50 hover:bg-accent/40"
          >
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <c.icon className="h-4 w-4" /> {c.label}
            </div>
            <div className="mt-3 font-display text-3xl font-semibold">{c.value}</div>
            <div className="mt-1 text-xs text-muted-foreground">{c.hint}</div>
          </Link>
        ))}
      </div>

      <div className="mt-10">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl font-semibold">Reservas recientes</h2>
          <Link to="/admin/bookings" className="text-sm font-medium text-primary hover:underline">
            Ver todas →
          </Link>
        </div>
        <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-card shadow-card">
          {data.recent.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Aún no hay reservas registradas.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left">Código</th>
                  <th className="px-4 py-3 text-left">Tour</th>
                  <th className="px-4 py-3 text-left">Cliente</th>
                  <th className="px-4 py-3 text-left">Estado</th>
                  <th className="px-4 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((b: any) => (
                  <tr key={b.id} className="border-t border-border">
                    <td className="px-4 py-3 font-mono text-xs">{b.booking_code}</td>
                    <td className="px-4 py-3">{b.tour_title}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{b.customer_name}</div>
                      <div className="text-xs text-muted-foreground">{b.customer_email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
                        {b.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">
                      ${Number(b.total).toLocaleString("es-MX")} {b.currency}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
